/**
 * @file caseCode.js
 * @description Pure helpers for "search by case ID" in Find Cases.
 *
 *   A case ID is the human-friendly code stamped on cases.case_code, shown on
 *   cards and on the Instagram case images: TYPE-NNNN (RBT-9001, BCBA-9002,
 *   BCABA-9003). The number comes from one shared sequence that started at 1000,
 *   so it is always 4+ digits.
 *
 *   Legacy: cases created before the RBT rename are stored as "BT-NNNN" and
 *   displayed as "RBT-NNNN" (theme.displayCaseCode). Both are accepted and
 *   normalised to the displayed form; the lookup RPC matches either stored form.
 *
 *   A BARE NUMBER IS NEVER A CASE ID: the prefix is required, so a ZIP or any
 *   other number can never be mistaken for one.
 * @module lib/caseCode
 */

// Dash-like separators a phone keyboard or paste can produce: hyphen-minus,
// hyphen, non-breaking hyphen, figure dash, en dash, em dash, horizontal bar,
// minus sign. Plus underscore, dot, colon, hash and whitespace.
const SEP = "[\\s\\-\\u2010-\\u2015\\u2212_.:#]*";
const PREFIX = "(RBT|BCBA|BCABA|BT)";

const COMPLETE = new RegExp(`^${PREFIX}${SEP}(\\d{4,6})$`, "i");

/**
 * @description Parses a typed/pasted case ID into its normalised displayed form.
 *   Accepts "bcba-9002", "BCBA 9002", "bcba9002", "BCaBA-9003" and the legacy "BT-9004" (returned as "RBT-9004").
 * @param {string} input - Raw text from the case ID search field
 * @returns {string|null} e.g. "BCBA-9002", or null when it is not a case ID
 */
export function parseCaseCodeQuery(input) {
  if (typeof input !== "string") return null;
  const m = input.trim().match(COMPLETE);
  if (!m) return null;
  const upper = m[1].toUpperCase();
  const prefix = upper === "BT" ? "RBT" : upper;
  return `${prefix}-${m[2]}`;
}

/**
 * @description Reads the case code out of a short-link path (/c/RBT-9001,
 *   /c/bcba9002, /c/BT-9004/). The same parser as the search box, so every
 *   spelling the edge function accepts is accepted here.
 * @param {string} pathname - window.location.pathname
 * @returns {string|null} Canonical code such as "BCBA-9002", or null when the
 *   path is not a short case link
 */
export function shortLinkCode(pathname) {
  if (typeof pathname !== "string") return null;
  const m = pathname.match(/^\/c\/([^/]+)\/?$/i);
  if (!m) return null;
  let seg = m[1];
  try { seg = decodeURIComponent(seg); } catch { /* keep raw */ }
  return parseCaseCodeQuery(seg);
}

/**
 * @description The link a person should share for a case: the short
 *   abacases.com/c/<CODE> link when the case has a code, else the permanent
 *   /share/<uuid> link. /share/<uuid> keeps working forever; this only decides
 *   which link NEW shares produce.
 * @param {{id?: string, caseCode?: string, case_code?: string}} caseData
 * @param {string} origin - e.g. window.location.origin
 * @returns {string|null} Absolute URL, or null with neither a code nor an id
 */
export function caseShareUrl(caseData, origin) {
  const code = parseCaseCodeQuery(caseData?.caseCode ?? caseData?.case_code ?? "");
  if (code) return `${origin}/c/${code}`;
  return caseData?.id ? `${origin}/share/${caseData.id}` : null;
}
